import { T, tt } from '@agos/shared'
import { BUTTON_PRIMARY, cn } from '@agos/ui'
import { AlertCircle, ChevronUp } from 'lucide-react'
import { useState } from 'react'

import type { RolePreview } from '../../roles/roles-api'
import type { RoleDiff } from '../permission-matrix'
import { refusalText } from '../role-model'

/** Where the server's preview is: not asked (nothing changed), on its way, answered, or failed. */
export type PreviewState = 'loading' | 'ready' | 'failed'

/**
 * The rail beside the role editor - *Zmeny* and *Koho sa to týka*, then the save. `FRONTEND.md` §9.752.
 *
 * **Drawn only while something changed**, and that is the old save bar's own rule kept: `[data-role-diff]` is what
 * `permissions.spec.ts` reads for the diff and the two buttons, and its absence is how that spec knows a change was
 * discarded. On a desktop it is a column beside the rights; on a phone the same element is a sheet pinned to the
 * bottom, *Náhľad a zmeny (n)*, padded by the safe area - one element in both places, so no locator finds two.
 *
 * **What the rail says is the server's where the server knows**: *Koho sa to týka* is `POST …/permissions/preview`,
 * which runs the same plan as the write (§10A.408), so the people named are the people the save reaches. *Zmeny* is
 * the client's own diff with FB-103's side effects, because which switch moved is the editor's to say.
 *
 * *Čo uvidí v menu* is deferred (§9.752 names it): it needs the shell to lend this screen its sidebar rule.
 */
export function PreviewRail({
  diff,
  labelOf,
  preview,
  state,
  onRetryPreview,
  saving,
  blocked,
  onDiscard,
  onSave,
}: {
  diff: RoleDiff
  labelOf: (key: string) => string
  /** The server's answer for the set being edited, or null while there is none. */
  preview: RolePreview | null
  state: PreviewState
  onRetryPreview: () => void
  saving: boolean
  /** Why the save is refused before it is pressed, or null. */
  blocked: string | null
  onDiscard: () => void
  onSave: () => void
}) {
  const [open, setOpen] = useState(false)
  const changes = diff.added.length + diff.removed.length
  const refused = preview?.refused ?? []

  return (
    <aside
      data-role-diff
      className={cn(
        'rounded-xl border-2 border-primary/40 bg-card p-3 space-y-3',
        'lg:sticky lg:top-4',
        'max-lg:fixed max-lg:inset-x-0 max-lg:bottom-0 max-lg:z-30 max-lg:rounded-b-none max-lg:max-h-[70vh] max-lg:overflow-y-auto',
        'max-lg:pb-[calc(0.75rem_+_env(safe-area-inset-bottom,0px))]',
      )}
    >
      <button
        type="button"
        className="lg:hidden flex w-full items-center justify-between text-sm font-bold"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        data-rail-toggle
      >
        <span>
          {tt('Náhľad a zmeny')} ({changes})
        </span>
        <ChevronUp className={cn('w-4 h-4 transition-transform', !open && 'rotate-180')} />
      </button>

      <p className="text-xs font-bold" data-diff-counts>
        {diff.added.length} <T>pridaných</T> · {diff.removed.length} <T>odobraných</T>
      </p>

      <div className={cn('space-y-3', !open && 'max-lg:hidden')}>
        <section className="space-y-1.5" data-rail-changes>
          <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{tt('Zmeny')}</h3>
          {diff.added.length > 0 && (
            <p className="text-xs text-emerald-500" data-diff-added>
              + {diff.added.map(labelOf).join(', ')}
            </p>
          )}
          {diff.removed.length > 0 && (
            <p className="text-xs text-red-500" data-diff-removed>
              − {diff.removed.map(labelOf).join(', ')}
            </p>
          )}
          {/* Legacy's preview stops at the ticked set; a parent change moves its children too. */}
          {diff.sideEffects.length > 0 && (
            <p className="text-xs text-amber-500" data-diff-side-effects>
              <T>Zároveň sa</T> {diff.sideEffects[0]?.nowHeld ? <T>udelí</T> : <T>odoberie</T>}{' '}
              <strong>{diff.sideEffects.length}</strong>{' '}
              <T>podradených práv, bez toho aby sa ich prepínač pohol:</T>{' '}
              {diff.sideEffects.map((effect) => labelOf(effect.permission)).join(', ')}
            </p>
          )}
          {refused.length > 0 && (
            <div className="space-y-0.5" data-preview-refused>
              {refused.map((right) => (
                <p key={right.key} className="flex items-start gap-1.5 text-xs text-red-500">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
                  <span>
                    {labelOf(right.key)}: {refusalText(right.code)}
                  </span>
                </p>
              ))}
            </div>
          )}
        </section>

        <section className="space-y-1.5" data-role-affected={state}>
          <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            {tt('Koho sa to týka')}
          </h3>
          {state === 'loading' && (
            <p className="text-xs text-muted-foreground">{tt('Server počíta, koho sa zmena týka...')}</p>
          )}
          {state === 'failed' && (
            <div className="text-xs space-y-1" data-preview-failed>
              <p className="text-red-500">{tt('Náhľad sa nepodarilo načítať - nevieme povedať, koho sa zmena týka.')}</p>
              <button
                type="button"
                onClick={onRetryPreview}
                className="px-2.5 py-1 rounded-lg border border-border font-semibold"
                data-preview-retry
              >
                {tt('Skúsiť znova')}
              </button>
            </div>
          )}
          {state === 'ready' && preview !== null && (
            <>
              {/* The built-in Administrátor's pass (#58): a save moves its menu and nothing the server allows. */}
              {preview.menuOnly && (
                <p className="text-xs text-amber-500" data-preview-menu-only>
                  {tt('Mení sa len menu - server tejto role dovolí aj nezaškrtnuté práva.')}
                </p>
              )}
              {preview.affected.length === 0 ? (
                <p className="text-xs text-muted-foreground" data-preview-nobody>
                  {tt('Nikomu sa nič nezmení - rolu nikto nemá, alebo to rozhodujú výnimky.')}
                </p>
              ) : (
                <ul className="space-y-1">
                  {preview.affected.map((person) => (
                    <li key={person.employeeId} className="text-xs" data-preview-person={person.employeeId}>
                      <strong>{person.name}</strong>
                      {person.gains.length > 0 && (
                        <span className="text-emerald-500"> + {person.gains.map(labelOf).join(', ')}</span>
                      )}
                      {person.loses.length > 0 && (
                        <span className="text-red-500"> − {person.loses.map(labelOf).join(', ')}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>
      </div>

      {blocked !== null && (
        <p className="text-xs text-red-500" data-save-blocked>
          {blocked}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onDiscard}
          className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted/50"
        >
          <T>Zahodiť</T>
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving || blocked !== null}
          className={cn('px-3 py-1.5 rounded-lg', BUTTON_PRIMARY, 'text-xs')}
          data-role-save
        >
          <T>Uložiť zmeny</T>
        </button>
      </div>
    </aside>
  )
}
